import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { IMG } from "@/lib/brand";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Section";
import { HexWatermark } from "@/components/BrandMark";
import { useLang } from "@/lib/LanguageContext";

export default function Services() {
  const { t } = useLang();
  const s = t.services;

  return (
    <div data-testid="services-page" className="bg-white">
      <PageHero
        testId="services-hero"
        eyebrow={s.hero_eyebrow}
        title={s.hero_title}
        intro={s.hero_intro}
        image={IMG.machinery}
      />

      <section className="relative container-x py-24 sm:py-32 overflow-hidden">
        <HexWatermark
          opacity={0.04}
          size={620}
          strokeWidth={1.2}
          className="text-primary -right-32 top-12"
        />
        <div className="relative grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionEyebrow>{s.sidebar_eyebrow}</SectionEyebrow>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight font-medium leading-[1.05]">
              {s.sidebar_title_l1}
              <br /> {s.sidebar_title_l2}
            </h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed">{s.sidebar_intro}</p>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="mt-10 inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 text-sm font-medium tracking-wide hover:bg-accent transition-colors"
            >
              {s.sidebar_cta} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <Accordion
              type="single"
              collapsible
              defaultValue="S01"
              className="border-y border-border"
              data-testid="services-accordion"
            >
              {s.items.map((item, idx) => {
                const code = `S${String(idx + 1).padStart(2, "0")}`;
                return (
                  <AccordionItem key={code} value={code} className="border-b border-border last:border-b-0">
                    <AccordionTrigger data-testid={`service-trigger-${code}`} className="py-6 hover:no-underline">
                      <div className="flex items-baseline gap-6 text-left">
                        <span className="text-xs tabular-nums tracking-[0.18em] uppercase text-muted-foreground">{code}</span>
                        <span className="font-display text-2xl sm:text-3xl tracking-tight font-medium">{item.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8">
                      <Reveal>
                        <div className="grid sm:grid-cols-12 gap-6">
                          <p className="sm:col-span-7 text-muted-foreground text-base leading-relaxed">{item.summary}</p>
                          <ul className="sm:col-span-5 space-y-2">
                            {item.points.map((pt) => (
                              <li key={pt} className="text-sm text-primary flex gap-3">
                                <span className="text-accent">▍</span>
                                {pt}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </Reveal>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="bg-secondary border-y border-border">
        <div className="container-x py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div className="img-zoom border border-border aspect-[4/3]">
            <img src={IMG.lab2} alt="Polymer laboratory" className="w-full h-full object-cover" />
          </div>
          <div>
            <SectionEyebrow>{s.process_eyebrow}</SectionEyebrow>
            <h3 className="mt-5 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-[1.05]">{s.process_title}</h3>
            <p className="mt-6 text-muted-foreground leading-relaxed">{s.process_intro}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
